/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useReducer } from "react";
import { useOutletContext, useLocation, useNavigate } from "react-router-dom";
import { isAxiosError } from "axios";
import { axiosErrorToStringError } from "@/utils/axiosErrorToStringError";
import { Save, ArrowBigRight, Trash } from "lucide-react";
import { useAdminGetCompetitionsSWR } from "@/services/competitionService";
import { useAdminCreateForumGroup } from "@/services/forumGroupService";
import { useAdminGetRolesDefinitionsSWR, useAdminGetRolesSWR } from "@/services/roleService";


const initialState = {
    apiPayload: {
        name: '',
        sidebar_category: false,
        forum_filter: false,
        forum_tag: false,
        hidden: false,
        color: '',
        forum_description: '',
        associated_for_skills: false,
        associated_competition: 0,
        create_skills_forum: false,
        forum_rights: [],
    },
    type_right: {
        role_type: '',
        right: ''
    },
    role_right: {
        role_id: '',
        right: ''
    },
    error_message: "",
    isLoading: false
}



const ACTIONS = {
    SET_API_PAYLOAD_FIELD: 'setApiPayloadField',
    SET_TYPE_RIGHT: 'setTypeRight',
    SET_ROLE_RIGHT: 'setRoleRight',
    SET_ERROR_MESSAGE: 'setErrorMessage',
    SET_IS_LOADING: 'setIsLoading',
    ADD_TYPE_RIGHT: 'addTypeRight',
    ADD_ROLE_RIGHT: 'addRoleRight',
    REMOVE_RIGHT: 'removeRoleRight',
}



const reducer = (state, action) => {
    switch (action.type) {
        case ACTIONS.SET_API_PAYLOAD_FIELD:
            return { ...state, apiPayload: { ...state.apiPayload, [action.subtype]: action.payload }, error_message: null }

        case ACTIONS.SET_ERROR_MESSAGE:
            return { ...state, error_message: action.payload }

        case ACTIONS.SET_IS_LOADING:
            return { ...state, isLoading: action.payload }

        case ACTIONS.SET_TYPE_RIGHT:
            return { ...state, type_right: { ...state.type_right, [action.subtype]: action.payload } }

        case ACTIONS.SET_ROLE_RIGHT:
            return { ...state, role_right: { ...state.role_right, [action.subtype]: action.payload } }

        case ACTIONS.ADD_TYPE_RIGHT:
            return { ...state, 
                type_right: { ...initialState.type_right }, 
                apiPayload: { ...state.apiPayload, 
                    forum_rights: [ ...state.apiPayload.forum_rights, {role_type: state.type_right.role_type, right: state.type_right.right} ]
                } 
            }
        
        case ACTIONS.ADD_ROLE_RIGHT:
            return { ...state, 
                role_right: { ...initialState.role_right }, 
                apiPayload: { ...state.apiPayload, 
                    forum_rights: [...state.apiPayload.forum_rights, {role_id: Number(state.role_right.role_id), right: state.role_right.right}]
                }
            }

        case ACTIONS.REMOVE_RIGHT:
            return { ...state, 
                apiPayload: { ...state.apiPayload, 
                    forum_rights: state.apiPayload.forum_rights.filter(
                        (right) => {
                            if (
                                (action.payload.role_type == right.role_type || action.payload.role_id == right.role_id)
                                && action.payload.right == right.right
                            ) {
                                return false
                            }
                            return true
                        } 
                    )
                } 
            }
        
        default:
            throw new Error("Unknown action.type " + action.type);
    }
}



const tailwindColors = {"slate": "bg-slate-500", "gray": "bg-gray-500", "zinc": "bg-zinc-500", "neutral": "bg-neutral-500", "stone": "bg-stone-500", "red": "bg-red-500", "orange": "bg-orange-500", "amber": "bg-amber-500", "yellow": "bg-yellow-500", "lime": "bg-lime-500", "green": "bg-green-500", "emerald": "bg-emerald-500", "teal": "bg-teal-500", "cyan": "bg-cyan-500", "sky": "bg-sky-500", "blue": "bg-blue-500", "indigo": "bg-indigo-500", "violet": "bg-violet-500", "purple": "bg-purple-500", "fuchsia": "bg-fuchsia-500", "pink": "bg-pink-500", "rose": "bg-rose-500"}


export default function Add() {
    const [, setPageTitle, , setShowBackButton] = useOutletContext()
    const [state, dispatch] = useReducer(reducer, initialState)

    const navigate = useNavigate()
    const location = useLocation()

    const adminCreateForumGroup = useAdminCreateForumGroup()
    const {data: competitions} = useAdminGetCompetitionsSWR()
    const {data: rolesDefinitions} = useAdminGetRolesDefinitionsSWR()
    const {data: roles} = useAdminGetRolesSWR()

    /* On met à jour le titre */
    useEffect(() => {
        setPageTitle("Ajout d'un groupe de forums")
        setShowBackButton(true)

        return () => {
            setPageTitle("Titre")
            setShowBackButton(false)
        }
    }, [setPageTitle, setShowBackButton])



    /* Gestion des changements de champs du formulaire */
    const handleChange = (e) => {
        dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: null });
        dispatch({ type: ACTIONS.SET_API_PAYLOAD_FIELD, subtype: e.target.name, payload: e.target.type == 'checkbox' ? e.target.checked : e.target.value })
    }



    /* Soumission du formulaire */
    const handleSubmit = async (e) => {
        e.preventDefault();

        if(!state?.apiPayload?.name) {
            dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: "Veuillez remplir tous les champs obligatoires" });
            return
        }

        let payload = state.apiPayload
        if (payload.associated_competition == 0) {
            payload.associated_competition = null
        }
        if (payload.forum_description == "" ) {
            payload.forum_description = null
        }
        if (payload.forum_rights.length == 0) {
            payload.forum_rights = null
        }

        dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: null });
        dispatch({ type: ACTIONS.SET_IS_LOADING, payload: true });

        try {
            await adminCreateForumGroup(state.apiPayload)
            navigate(location.state?.from ?? "/admin/forum_groups")
        } catch (error) {
            console.error('admin/forums/add::handleSubmit::error', error)
            if (error?.response?.status === 412) {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: "Erreur de validation en base de données (code déjà utilisé, contrainte non respectée)" });
            } else if (isAxiosError(error)) {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: axiosErrorToStringError(error) });
            } else {
                dispatch({ type: ACTIONS.SET_ERROR_MESSAGE, payload: error?.message || "Erreur inconnue" });
            }
        } finally {
            dispatch({ type: ACTIONS.SET_IS_LOADING, payload: false });
        }
    }


    return (
        <>
            <h2 className="text-secondary text-lg flex-none content-center mb-4">
                Paramètres du groupe de forums
            </h2>

            <form className="flex flex-col gap-4" onSubmit={(e) => handleSubmit(e)}>

                {/* Ligne : nom */}
                <div className="md:w-[70%] flex flex-row flex-wrap gap-5 content-stretch">
                    <div className="flex flex-col gap-2 flex-auto">
                        <label className="text-primary text-base" htmlFor="name">
                            Nom <span className="text-red-500">*</span>
                        </label>
                        <input type="text" 
                            className={`w-full border border-secondary rounded-md`}
                            id="name" name="name" 
                            value={state.apiPayload.name} 
                            onChange={handleChange}
                            required
                            />
                    </div>
                </div>

                {/* Ligne : description */}
                <div className="md:w-[70%] flex flex-row flex-wrap gap-5 content-stretch">
                    <div className="flex flex-col gap-2 flex-auto">
                        <label className="text-primary text-base" htmlFor="forum_description">
                            Description des forums
                        </label>
                        <input type="text" 
                            className={`w-full border border-secondary rounded-md`}
                            id="forum_description" name="forum_description" 
                            value={state.apiPayload.forum_description} 
                            onChange={handleChange}
                            required
                            />
                    </div>
                </div>
                
                {/* Ligne : couleur */}
                <div className="sm:w-[20%] flex flex-row flex-wrap gap-5 content-stretch">
                    <div className="flex flex-col gap-2 flex-auto">
                        <label className="text-primary text-base flex flex-row items-center gap-2" htmlFor="color">
                            <span className={`w-4 h-4 rounded-full ${tailwindColors[state.apiPayload.color]} inline-block`}></span>
                            <span>Couleur</span> 
                        </label>
                        <select className="border border-secondary rounded-md text-base"
                            id="color" name="color" 
                            value={state.apiPayload.color} 
                            onChange={handleChange}
                            required
                        >
                            <option value="">-</option>
                            {Object.keys(tailwindColors).map((color, i) => {
                                return (
                                    <option key={i} value={color}>
                                        {color}
                                    </option>
                                )
                            })}
                        </select>
                    </div>
                </div>

                {/* Ligne : Sidebar, Filter, Tag et Hidden */}
                <div className="md:w-[70%] flex flex-row flex-wrap gap-5 content-stretch">
                    <div className="flex flex-row flex-nowrap gap-2 flex-auto">
                        <input type="checkbox" 
                            className={`w-6 h-6 border border-secondary rounded-md`}
                            id="sidebar_category" name="sidebar_category" 
                            checked={state.apiPayload.sidebar_category} 
                            onChange={handleChange}
                            />
                        <label className="text-primary text-base" htmlFor="sidebar_category">
                            Sidebar
                        </label>
                    </div>
                    <div className="flex flex-row flex-nowrap gap-2 flex-auto">
                        <input type="checkbox" 
                            className={`w-6 h-6 border border-secondary rounded-md`}
                            id="forum_filter" name="forum_filter" 
                            checked={state.apiPayload.forum_filter} 
                            onChange={handleChange}
                            />
                        <label className="text-primary text-base" htmlFor="forum_filter">
                            Filtre
                        </label>
                    </div>
                    <div className="flex flex-row flex-nowrap gap-2 flex-auto">
                        <input type="checkbox" 
                            className={`w-6 h-6 border border-secondary rounded-md`}
                            id="forum_tag" name="forum_tag" 
                            checked={state.apiPayload.forum_tag} 
                            onChange={handleChange}
                            />
                        <label className="text-primary text-base" htmlFor="forum_tag">
                            Tag
                        </label>
                    </div>
                    <div className="flex flex-row flex-nowrap gap-2 flex-auto">
                        <input type="checkbox" 
                            className={`w-6 h-6 border border-secondary rounded-md`}
                            id="hidden" name="hidden" 
                            checked={state.apiPayload.hidden} 
                            onChange={handleChange}
                            />
                        <label className="text-primary text-base" htmlFor="hidden">
                            Caché
                        </label>
                    </div>
                </div>

                {/* Ligne : associated_for_skills */}
                <div className="md:w-[70%] flex flex-row flex-wrap gap-5 content-stretch">
                    <div className="flex flex-row gap-2 flex-auto">
                        <input type="checkbox" 
                            className={`w-6 h-6 border border-secondary rounded-md`}
                            id="associated_for_skills" name="associated_for_skills" 
                            checked={state.apiPayload.associated_for_skills} 
                            onChange={handleChange}
                            />
                        <label className="text-primary text-base" htmlFor="associated_for_skills">
                            Faire l&apos;association des forums avec les métiers 
                        </label>
                    </div>
                </div>
            
                {/* Ligne : associated_competition et create_skills_forum if create_all_skills is true */}
                { state.apiPayload.associated_for_skills && (
                    <div className="flex flex-col gap-2 bg-gray-100 border border-secondary rounded-md px-2 py-1 ">
                        <div className=" flex flex-row flex-wrap gap-5">
                            <div className="flex flex-col gap-2">
                                <label className="text-primary text-base" htmlFor="associated_competition">
                                    Limiter à une compétition spécifique
                                </label>
                                <select className="border border-secondary rounded-md text-base"
                                    id="associated_competition" name="associated_competition" 
                                    value={state.apiPayload.associated_competition} 
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="">-</option>
                                    {competitions?.map((competition, i) => {
                                        return (
                                            <option key={i} value={competition.id}>
                                                {competition.code} - {competition.name}
                                            </option>
                                        )
                                    })}
                                </select>
                            </div>
                        </div>
                        <div className="md:w-[70%] flex flex-row flex-wrap gap-5 content-stretch">
                            <div className="flex flex-row gap-2 flex-auto">
                                <input type="checkbox" 
                                    className={`w-6 h-6 border border-secondary rounded-md`}
                                    id="create_skills_forum" name="create_skills_forum" 
                                    checked={state.apiPayload.create_skills_forum} 
                                    onChange={handleChange}
                                    />
                                <label className="text-primary text-base" htmlFor="create_skills_forum">
                                    Créer les forums pour chaque métier
                                </label>
                            </div>
                        </div>
                    </div>
                )}

                {/* Ligne : forum_rights */}
                <div className="flex flex-row flex-wrap gap-5 content-stretch">
                    <div className="flex flex-col flex-1 border border-secondary rounded-md px-2 py-1 bg-gray-100 gap-2">
                        { state.apiPayload.associated_for_skills && (
                            <>
                                <h3 className="text-secondary">Rôles spécifiques aux métiers</h3>
                                <div className="flex flex-row items-center gap-2">
                                    
                                    <select className="border border-secondary rounded-md text-base p-1"
                                        id="type_right" name="type_right" 
                                        value={state.type_right.role_type} 
                                        onChange={
                                            (e) => { 
                                                dispatch({ type: ACTIONS.SET_TYPE_RIGHT, subtype: "role_type", payload: e.target.value })
                                            }
                                        }
                                        required
                                    >
                                        <option value="">-</option>
                                        {rolesDefinitions && Object.keys(rolesDefinitions)?.filter(
                                            (role) => {
                                                if (rolesDefinitions[role].for_skills && state?.apiPayload?.forum_rights?.filter((right) => right.role_type == role).length == 0) {
                                                    return true
                                                }
                                                return false

                                            }
                                        ).map((role, i) => {
                                            return (
                                                <option key={i} value={role}>
                                                    {role}
                                                </option>
                                            )
                                        })}
                                    </select>

                                    { state.type_right.role_type && (
                                        <div className="flex flex-row items-center gap-2">
                                            <select className="border border-secondary rounded-md text-base p-1"
                                                id="right" name="right" 
                                                value={state.type_right.right} 
                                                onChange={
                                                    (e) => { 
                                                        dispatch({ type: ACTIONS.SET_TYPE_RIGHT, subtype: "right", payload: e.target.value })
                                                    }
                                                }
                                                required
                                            >
                                                <option value="">-</option>
                                                <option value="topic_create">topic_create</option>
                                                <option value="forum_read">forum_read</option>
                                                <option value="forum_write">forum_write</option>
                                                <option value="forum_moderate">forum_moderate</option>
                                            </select>
                                        </div>
                                    )}
                                    {
                                        state.type_right.role_type && state.type_right.right && (
                                            <button
                                                onClick={(e) => {e.preventDefault();dispatch({ type: ACTIONS.ADD_TYPE_RIGHT })}}
                                            >
                                                <ArrowBigRight size={24} className="text-primary hover:text-green-600" />
                                            </button>
                                        )
                                    }
                                </div>
                            </>
                        )}
                        
                        <h3 className="text-secondary">Rôles génériques</h3>
                        <div className="flex flex-row items-center gap-2">
                            
                            <select className="border border-secondary rounded-md text-base p-1"
                                id="role_id" name="role_id" 
                                value={state.role_right.role_id} 
                                onChange={
                                    (e) => { 
                                        dispatch({ type: ACTIONS.SET_ROLE_RIGHT, subtype: "role_id", payload: e.target.value })
                                    }
                                }
                                required
                            >
                                <option value="">-</option>
                                {roles && roles?.filter(
                                    (role) => {
                                        if (rolesDefinitions[role.type].for_skills || state?.apiPayload?.forum_rights?.filter((right) => right.role_id == role.id).length > 0) {
                                            return false
                                        }
                                        return true
                                    }
                                ).map((role, i) => {
                                    return (
                                        <option key={i} value={role.id}>
                                            {role.name}
                                        </option>
                                    )
                                })}
                            </select>

                            { state.role_right.role_id && (
                                <div className="flex flex-row items-center gap-2">
                                    <select className="border border-secondary rounded-md text-base p-1"
                                        id="right" name="right" 
                                        value={state.role_right.right} 
                                        onChange={
                                            (e) => { 
                                                dispatch({ type: ACTIONS.SET_ROLE_RIGHT, subtype: "right", payload: e.target.value })
                                            }
                                        }
                                        required
                                    >
                                        <option value="">-</option>
                                        <option value="topic_create">topic_create</option>
                                        <option value="forum_read">forum_read</option>
                                        <option value="forum_write">forum_write</option>
                                        <option value="forum_moderate">forum_moderate</option>
                                    </select>
                                </div>
                            )}
                            {
                                state?.role_right?.role_id && state?.role_right?.right && (
                                    <button
                                        onClick={(e) => {e.preventDefault();dispatch({ type: ACTIONS.ADD_ROLE_RIGHT })}}
                                    >
                                        <ArrowBigRight size={24} className="text-primary hover:text-green-600" />
                                    </button>
                                )
                            }
                        </div>

                    </div>
                    <div className="flex flex-col flex-1 border border-secondary rounded-md px-2 py-1 bg-gray-100 gap-3">
                        <h3 className="text-secondary">Drois appliqués</h3>
                        <div className="flex flex-row flex-wrap items-center gap-2">
                            {state.apiPayload.forum_rights.map((right, i) => {
                                let name = right?.role_type ? right.role_type : right.role_id
                                if (right?.role_id) {
                                    name = roles?.find((role) => role.id === right.role_id)?.name
                                }
                                return (
                                    <span key={i} className="flex flex-row flex-nowrap items-center gap-1 text-xs border border-secondary rounded-md px-2 py-1 bg-gray-100">
                                        {`${name}: `}
                                        {right.right}
                                        <Trash size={16} className="cursor-pointer text-red-600 hover:text-red-700" onClick={(e) => {e.preventDefault();dispatch({ type: ACTIONS.REMOVE_RIGHT, payload: right })}} />
                                    </span>
                                )
                            })}
                        </div>
                    </div>
                </div>

                {/* Ligne : boutons et message d'erreur */}
                <div className="w-[50%] gap-5 flex flex-row items-center">
                    
                    <button className="flex gap-2 items-center border border-blue-500 rounded-md px-2 py-1 text-base hover:bg-blue-200"
                        onClick={(e) => handleSubmit(e)}
                        disabled={state.isLoading}
                    >
                        <Save size={24} className="text-primary" />
                        Sauvegarder
                    </button>
                    
                    {
                        state?.error_message && <span className="text-red-700 flex-none content-center mr-4">{state.error_message}</span>
                    }
                </div>
            </form>
        </>
    )
}